import React, { useMemo } from 'react';
import {
  Drawer,
  Button,
  Space,
  Table,
  Card,
  Tag,
  Empty,
  Alert,
  Row,
  Col,
  Collapse,
  Tooltip,
  Statistic,
  Divider,
  message,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { getOfflineQueueSyncHistory, useOfflineQueueControl } from '../hooks/useOfflineMutationQueue';
import { useResponsiveSettings } from '../hooks/useResponsiveSettings';

export const OfflineDataViewer = ({ triggerButton = true, open, onOpenChange }) => {
  const [internalDrawerOpen, setInternalDrawerOpen] = React.useState(false);
  const { entries, queueCount, queueSizeBytes, isSyncing, syncAllOfflineQueues } =
    useOfflineQueueControl();
  const { isMobile } = useResponsiveSettings();
  const drawerOpen = open ?? internalDrawerOpen;
  const setDrawerOpen = onOpenChange ?? setInternalDrawerOpen;


  const allOfflineData = useMemo(() => {
    const data = [];

    entries.forEach((entry) => {
      try {
        const raw = localStorage.getItem(entry.storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            data.push({
              storageKey: entry.storageKey,
              items: parsed,
              count: parsed.length,
            });
          }
        }
      } catch (error) {
        console.error(`Failed to parse offline data for ${entry.storageKey}:`, error);
      }
    });

    return data;
  }, [entries]);

  const allSyncHistory = useMemo(() => {
    return entries.map((entry) => ({
      storageKey: entry.storageKey,
      history: getOfflineQueueSyncHistory(entry.storageKey),
    }));
  }, [entries]);

  /**
   * Format bytes to human readable format
   */
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Download offline data as JSON file
   */
  const handleDownloadJSON = () => {
    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        summary: {
          totalItems: queueCount,
          estimatedSizeBytes: queueSizeBytes,
          queueCount: entries.length,
          isSyncing,
        },
        queues: allOfflineData.map((queue) => ({
          storageKey: queue.storageKey,
          itemCount: queue.count,
          items: queue.items,
        })),
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `offline-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('Offline data downloaded successfully');
    } catch (error) {
      console.error('Failed to download offline data:', error);
      message.error('Failed to download offline data');
    }
  };

  
  const handleClearQueue = (storageKey) => {
    try {
      localStorage.removeItem(storageKey);
      message.success(`Queue ${storageKey} cleared`);
    } catch (error) {
      console.error('Failed to clear queue:', error);
      message.error('Failed to clear queue');
    }
  };

  const getItemColumns = () => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (id) => (
        <Tooltip title={id}>
          <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>
            {id?.substring(0, 8)}...
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const isAuthError = record.meta?.isAuthError;
        const attempts = record.meta?.attempts || 0;

        if (isAuthError) {
          return <Tag color="error">Auth Error</Tag>;
        }
        if (attempts > 0) {
          return <Tag color="warning">Retrying ({attempts})</Tag>;
        }
        return <Tag color="default">Pending</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: ['createdAt'],
      key: 'createdAt',
      width: 150,
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString();
      },
    },
    {
      title: 'Payload Preview',
      key: 'payload',
      render: (_, record) => {
        const preview = JSON.stringify(record.payload || {}).substring(0, 100);
        return (
          <Tooltip title={JSON.stringify(record.payload, null, 2)}>
            <span
              style={{
                fontSize: '12px',
                fontFamily: 'monospace',
                color: '#666',
                cursor: 'pointer',
              }}
            >
              {preview}...
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Error',
      dataIndex: ['meta', 'lastError'],
      key: 'lastError',
      width: 150,
      render: (error) => {
        if (!error) return '-';
        return (
          <Tooltip title={error}>
            <span style={{ fontSize: '12px', color: '#d32f2f' }}>
              {error.substring(0, 30)}...
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const getHistoryColumns = () => [
    {
      title: 'Synced At',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString();
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        const normalized = status || 'unknown';

        if (normalized === 'success') return <Tag color="success">Successful</Tag>;
        if (normalized === 'partial') return <Tag color="warning">Partial</Tag>;
        if (normalized === 'auth-error') return <Tag color="error">Auth Error</Tag>;
        if (normalized === 'failed') return <Tag color="error">Failed</Tag>;
        return <Tag color={record?.hasAuthError ? 'error' : 'default'}>{normalized}</Tag>;
      },
    },
    {
      title: 'Synced Items',
      key: 'syncedItems',
      width: 220,
      render: (_, record) => {
        const items = record.syncedItems || [];
        const preview = items
          .map((item) => item.id || item.payload?.id || item.payload?.name || 'item')
          .slice(0, 3);

        return (
          <Tooltip
            title={
              items.length
                ? JSON.stringify(items, null, 2)
                : 'No synced items available'
            }
          >
            <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {preview.length ? preview.join(', ') : '-'}
              {items.length > preview.length ? ` +${items.length - preview.length}` : ''}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Count',
      key: 'count',
      width: 90,
      render: (_, record) => `${record.syncedCount || 0}/${record.total || 0}`,
    },
    {
      title: 'Error',
      key: 'error',
      width: 180,
      render: (_, record) => {
        const reason = record.hasAuthError
          ? 'Unauthorized (401)'
          : record.failedAttempts?.[0]?.reason;

        if (!reason) return '-';

        return (
          <Tooltip title={reason}>
            <span style={{ fontSize: '12px', color: '#d32f2f' }}>
              {reason.substring(0, 40)}...
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const handleSyncNow = async () => {
    try {
      await syncAllOfflineQueues();
      message.success('Sync completed');
    } catch (error) {
      console.error('Sync failed:', error);
      message.error('Sync failed');
    }
  };

  const cusr = () => {
    return allOfflineData.map((queue) => ({
      history: allSyncHistory.find((entry) => entry.storageKey === queue.storageKey)?.history || [],
      key: queue.storageKey,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <span>
            <strong>{queue.storageKey}</strong> ({queue.count} item{queue.count !== 1 ? 's' : ''})
          </span>
          <Tag color={queue.count > 0 ? 'orange' : 'default'}>{queue.count}</Tag>
        </div>
      ),
      children: (
        <div>
          {queue.items.length === 0 ? (
            <Empty description="No items in queue" />
          ) : (
            <Table
              columns={getItemColumns()}
              dataSource={queue.items.map((item, idx) => ({
                ...item,
                key: item.id || idx,
              }))}
              size="small"
              pagination={false}
              scroll={{ x: true }}
            />
          )}
          <div style={{ marginTop: '16px' }}>
            <strong>Previously Synced:</strong>
            {allSyncHistory.find((entry) => entry.storageKey === queue.storageKey)?.history.length ? (
              <Table
                style={{ marginTop: '8px' }}
                columns={getHistoryColumns()}
                dataSource={allSyncHistory
                  .find((entry) => entry.storageKey === queue.storageKey)
                  ?.history.map((item, idx) => ({
                    ...item,
                    key: `${queue.storageKey}-history-${idx}`,
                  })) || []}
                size="small"
                pagination={false}
                scroll={{ x: true }}
              />
            ) : (
              <Empty
                description="No previous sync history for this queue"
                style={{ padding: '12px 0' }}
              />
            )}
          </div>
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              danger
              size="small"
              onClick={() => handleClearQueue(queue.storageKey)}
              disabled={queue.items.length === 0}
            >
              Clear This Queue
            </Button>
          </div>
        </div>
      ),
    }));
  };

  const drawerContent = (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        size="small"
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DatabaseOutlined />
            Offline Data Summary
          </div>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total Items"
              value={queueCount}
              suffix={isSyncing ? <Tag color="processing">Syncing</Tag> : ''}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Queues Active"
              value={entries.filter((e) => e.queueCount > 0).length}
              suffix={`/ ${entries.length}`}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total Size"
              value={formatBytes(queueSizeBytes)}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Status"
              value={
                queueCount === 0 ? (
                  <Tag color="success">No Pending Data</Tag>
                ) : (
                  <Tag color="warning">Pending Sync</Tag>
                )
              }
            />
          </Col>
        </Row>
      </Card>

      <Divider style={{ margin: '12px 0' }} />

      {queueCount > 0 && (
        <Alert
          message="Offline Data Detected"
          description={`You have ${queueCount} item${queueCount !== 1 ? 's' : ''} waiting to be synced when online.`}
          type="info"
          showIcon
          closable
        />
      )}

      {queueCount === 0 && (
        <Empty
          description="No offline data"
          style={{ padding: '24px', marginTop: '12px' }}
        />
      )}

      {queueCount > 0 && (
        <>
          <div>
            <strong>Offline Queues:</strong>
          </div>
          <Collapse items={queueCount} />
        </>
      )}

      {/* Actions */}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Divider style={{ margin: '12px 0' }} />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadJSON}
            disabled={queueCount === 0}
          >
            Download
          </Button>

          <Button
            icon={<ReloadOutlined />}
            onClick={handleSyncNow}
            loading={isSyncing}
            disabled={queueCount === 0}
          >
            Sync Now
          </Button>

          {/* <Button
            danger
            icon={<ClearOutlined />}
            onClick={handleClearAllData}
            disabled={queueCount === 0}
          >
            Clear All
          </Button> */}
        </div>
      </Space>

      <Card size="small" type="inner">
        <strong>About Offline Data:</strong>
        <ul style={{ marginTop: '8px', marginBottom: 0, fontSize: '12px' }}>
          <li>Data saved while offline will be synced automatically when online</li>
          <li>You can manually trigger sync using the "Sync Now" button</li>
          <li>Downloads data in JSON format</li>
          <li>Device must be connected to complete the sync</li>
        </ul>
      </Card>
    </Space>
  );

  return (
    <>
      {triggerButton && (
        <div
          style={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            zIndex: 9998,
          }}
        >
          <Tooltip title="View offline data">
            <Button
              type="default"
              shape="circle"
              size="large"
              icon={<DatabaseOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{
                backgroundColor: queueCount > 0 ? '#faad14' : '#fff',
                borderColor: queueCount > 0 ? '#faad14' : '#d9d9d9',
                color: queueCount > 0 ? '#fff' : undefined,
              }}
            >
              {queueCount > 0 && <span style={{ marginLeft: '4px', fontSize: '12px' }}>{queueCount}</span>}
            </Button>
          </Tooltip>
        </div>
      )}

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DatabaseOutlined />
            Offline Data Viewer
            {queueCount > 0 && <Tag color="warning">Pending</Tag>}
          </div>
        }
        placement={isMobile ? 'bottom' : 'right'}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={isMobile ? '100%' : Math.min(800, typeof window !== 'undefined' ? window.innerWidth - 20 : 800)}
        height={isMobile ? '90vh' : undefined}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default OfflineDataViewer;
